import { Receipt, ExpenseClaim } from '../types/expense';
import { authFetch } from '../lib/auth-fetch';

export class ApiService {
  constructor(private baseUrl: string, private isLiveMode: boolean = false) {}

  private buildUrl(path: string): string {
    // Join baseUrl and path without letting a leading slash in `path`
    // override the baseUrl's path portion when using the URL constructor.
    const base = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl;
    const relative = path.startsWith('/') ? path.slice(1) : path;
    const combined = `${base}/${relative}`;
    const url = new URL(combined);
    if (this.isLiveMode) {
      url.searchParams.append('mode', 'live');
    }
    return url.toString();
  }

  async getUnprocessedReceipts(): Promise<Receipt[]> {
    const response = await authFetch(this.buildUrl('/api/receipts/unprocessed'));
    if (!response.ok) throw new Error('Failed to fetch unprocessed receipts');
    return response.json();
  }

  async getProcessedReceipts(): Promise<Receipt[]> {
    const response = await authFetch(this.buildUrl('/api/receipts/processed'));
    if (!response.ok) throw new Error('Failed to fetch processed receipts');
    return response.json();
  }

  async processReceipts(receiptIds: string[]): Promise<ExpenseClaim[]> {
        const response = await authFetch(this.buildUrl('/api/receipts/process'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ receiptIds }),
        });
    if (!response.ok) throw new Error('Failed to process receipts');
    return response.json();
  }

  async processExpense(receiptId: string): Promise<{ message: string; receiptId: string; expenseClaimId: string; gqlClaimId: string }> {
        const response = await authFetch(this.buildUrl(`/api/ProcessExpense/${receiptId}`), {
          method: 'POST',
        });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Failed to process expense');
    }
    return response.json();
  }

  async getExpenseClaims(): Promise<ExpenseClaim[]> {
    const response = await authFetch(this.buildUrl('/api/expenses'));
    if (!response.ok) throw new Error('Failed to fetch expense claims');
    return response.json();
  }

  async updateExpenseStatus(id: string, status: string): Promise<ExpenseClaim> {
        const response = await authFetch(this.buildUrl(`/api/expenses/${id}/status`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        });
    if (!response.ok) throw new Error('Failed to update expense status');
    return response.json();
  }

  async updateExpenseClaim(expense: ExpenseClaim): Promise<ExpenseClaim> {
        const response = await authFetch(this.buildUrl(`/api/expenses/${expense.id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(expense),
        });
    if (!response.ok) throw new Error('Failed to update expense claim');
    // Backend returns 204 No Content, so we return the updated expense data
    return expense;
  }

  async uploadReceipt(file: File, receiptTypeId?: string): Promise<Receipt> {
    const formData = new FormData();
    formData.append('file', file);
    if (receiptTypeId) {
      formData.append('receiptType', receiptTypeId);
    }
    
        const response = await authFetch(this.buildUrl('/api/receipts/upload'), {
          method: 'POST',
          body: formData,
        });
    if (!response.ok) throw new Error('Failed to upload receipt');
    return response.json();
  }

  async updateReceipt(id: string, receipt: Partial<Receipt>): Promise<Receipt> {
        const response = await authFetch(this.buildUrl(`/api/receipts/${id}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(receipt),
        });
    if (!response.ok) throw new Error('Failed to update receipt');
    // Backend returns 204 No Content, so we return the updated receipt data
    return { ...receipt, id } as Receipt;
  }

  async deleteReceipt(id: string): Promise<void> {
        const response = await authFetch(this.buildUrl(`/api/receipts/${id}`), {
          method: 'DELETE',
        });
    if (!response.ok) throw new Error('Failed to delete receipt');
  }

  async scanReceiptOcr(receiptId: string): Promise<ReceiptOcrResult> {
        const response = await authFetch(this.buildUrl(`/api/receipts/${receiptId}/ocr`), {
          method: 'POST',
        });
    if (!response.ok) throw new Error('Failed to scan receipt with OCR');
    return response.json();
  }
}

export interface DuplicateReceipt {
  receiptId: string;
  merchantName?: string;
  amount?: number;
  transactionDate?: string;
  uploadedAt: string;
  matchReason: string;
  matchScore: number;
}

export interface ReceiptOcrResult {
  id: string;
  receiptId: string;
  merchantName?: string;
  totalAmount?: number;
  currency?: string;
  transactionDate?: string;
  taxAmount?: number;
  subtotal?: number;
  tip?: number;
  originalImageBase64?: string;
  annotatedImageBase64?: string;
  confidence?: number;
  rawOcrData?: string;
  createdAt: string;
  updatedAt: string;
  potentialDuplicates?: DuplicateReceipt[];
}
